<template>
  <div
    class="base-inline-status"
    :class="`base-inline-status--${kind}`"
    aria-live="polite"
  >
    <span
      v-if="kind === 'loading'"
      class="base-inline-status__spinner"
      aria-hidden="true"
    ></span>

    <span
      v-else-if="kind === 'pending'"
      class="base-inline-status__dots"
      aria-hidden="true"
    >
      <span></span>
      <span></span>
      <span></span>
    </span>

    <span
      v-else
      class="base-inline-status__icon"
      aria-hidden="true"
    >
      <AppIcon :name="iconName" :size="16" />
    </span>

    <span class="base-inline-status__text">
      {{ text }}
    </span>
  </div>
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";

const ICON_NAMES = {
  info: "infoCircle",
  success: "circleCheck",
  warning: "infoFilled",
  danger: "close",
  error: "close",
  empty: "info",
};

export default {
  name: "BaseInlineStatus",

  components: {
    AppIcon,
  },

  props: {
    kind: {
      type: String,
      default: "info",
      validator: (value) =>
        [
          "info",
          "success",
          "warning",
          "danger",
          "error",
          "loading",
          "pending",
          "empty",
        ].includes(value),
    },
    text: {
      type: String,
      required: true,
    },
  },

  computed: {
    iconName() {
      return ICON_NAMES[this.kind] || null;
    },

  },
};
</script>

<style scoped>
.base-inline-status {
  display: inline-flex;
  align-items: flex-start;
  gap: 7px;
  max-width: 100%;
  width: fit-content;
  color: #596573;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
}

.base-inline-status--info,
.base-inline-status--empty {
  color: #52606d;
}

.base-inline-status--pending,
.base-inline-status--warning {
  color: #8a5a00;
}

.base-inline-status--success {
  color: #087e70;
}

.base-inline-status--loading {
  color: var(--color-green-hover);
}

.base-inline-status--danger,
.base-inline-status--error {
  color: #b42318;
}

.base-inline-status__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #f1f4f6;
}

.base-inline-status--success .base-inline-status__icon {
  background: #eaf8f5;
}

.base-inline-status--pending .base-inline-status__icon,
.base-inline-status--warning .base-inline-status__icon {
  background: #fff8e8;
}

.base-inline-status--danger .base-inline-status__icon,
.base-inline-status--error .base-inline-status__icon {
  background: #fff0ee;
}

.base-inline-status__dots {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
  height: 18px;
  margin-top: 2px;
}

.base-inline-status__dots span {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
  animation: inline-status-dot 1.1s ease-in-out infinite;
}

.base-inline-status__dots span:nth-child(2) {
  animation-delay: 0.15s;
}

.base-inline-status__dots span:nth-child(3) {
  animation-delay: 0.3s;
}

.base-inline-status__spinner {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  border: 2px solid #d5eee9;
  border-top-color: var(--color-green-hover);
  border-radius: 50%;
  margin-top: 4px;
  animation: inline-status-spin 0.8s linear infinite;
}

.base-inline-status__text {
  min-width: 0;
  padding-top: 2px;
  overflow-wrap: anywhere;
}

@keyframes inline-status-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes inline-status-dot {
  0%,
  80%,
  100% {
    opacity: 0.35;
    transform: translateY(0);
  }

  40% {
    opacity: 1;
    transform: translateY(-2px);
  }
}

</style>
